/**
 * Typed caller for the member-initiated membership-freeze request
 * (plan 03 / MA-08): POST /api/v1/memberships/:id/freeze.
 *
 * The member submits { start_date, end_date, reason }; the membership Edge
 * Function records a `membership_freezes` row in status 'requested' and returns
 * it. Approval/rejection is a staff action (admin-web). Same unwrap() + error
 * envelope as the other functions/* callers. Read of freeze status goes via
 * rest/member-self.ts (getMyFreezes); this is the write path only.
 */
import { ApiError, type ErrorEnvelope, type FreezeStatus, isErrorCode } from '@gym/shared';

import type { GymSupabaseClient } from '../supabase.js';

function unwrap<T>(data: T | ErrorEnvelope | null, error: { message: string } | null): T {
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  if (data && typeof data === 'object' && 'error' in data && (data as ErrorEnvelope).error) {
    const env = (data as ErrorEnvelope).error;
    throw new ApiError(isErrorCode(env.code) ? env.code : 'internal_error', {
      message: env.message,
      ...(env.details !== undefined ? { details: env.details } : {}),
      requestId: env.request_id,
      retriable: env.retriable,
    });
  }
  return data as T;
}

export interface FreezeRequestBody {
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
  reason: string;
}

export interface FreezeRequestResult {
  id: string;
  membership_id: string;
  status: FreezeStatus;
  start_date: string | null;
  end_date: string | null;
  days: number | null;
  reason: string | null;
  requested_at: string | null;
  approved_at: string | null;
}

/** POST /api/v1/memberships/:id/freeze — request a membership freeze (MA-08). */
export async function requestFreeze(
  client: GymSupabaseClient,
  membershipId: string,
  body: FreezeRequestBody,
  idempotencyKey: string = crypto.randomUUID(),
): Promise<FreezeRequestResult> {
  const { data, error } = await client.functions.invoke<FreezeRequestResult | ErrorEnvelope>(
    `memberships/${membershipId}/freeze`,
    { headers: { 'Idempotency-Key': idempotencyKey }, body },
  );
  return unwrap(data, error);
}
